import { useCallback } from "react";
import { faRobot } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Text, Tooltip, UnstyledButton } from "@mantine/core";

import {
  Autotriage,
  Autotriage_Verdict,
  autotriage_VerdictFromJSON,
  AutotriageFeedback_Rating,
  autotriageFeedback_RatingFromJSON,
} from "@protos/ai/v1/triage";
import { useAutotriageFeedbackMutation } from "@shared/ai";
import { updateDeploymentFinding } from "@shared/api";
import { useOrgAssertDefined } from "@shared/hooks";

interface Props {
  autotriage: Autotriage | null;
}

const FeedbackButtons = ({ autotriage }: Props) => {
  const org = useOrgAssertDefined();
  const { mutateAsync: mutateAutotriageFeedback } =
    useAutotriageFeedbackMutation();

  const putAutotriageFeedback = useCallback(
    (desiredRating: AutotriageFeedback_Rating) => async () => {
      if (!autotriage) return;

      if (desiredRating === AutotriageFeedback_Rating.GOOD) {
        await updateDeploymentFinding(org.id, autotriage.issueId, {
          triageState: "ignored",
          triageReason: "false_positive",
          note: autotriage.reason,
        });
      }

      await mutateAutotriageFeedback({
        orgId: org.id,
        request: {
          feedback: {
            autotriageId: autotriage.id,
            rating: desiredRating,
          },
        },
      });
    },
    [org, autotriage, mutateAutotriageFeedback]
  );
  if (
    autotriage?.feedback?.rating &&
    autotriageFeedback_RatingFromJSON(autotriage?.feedback.rating) ===
      AutotriageFeedback_Rating.GOOD
  )
    return (
      <Text component="span" fz={12} style={{ fontStyle: "oblique 20deg" }}>
        We recorded that your team agrees.
      </Text>
    );
  if (
    autotriage?.feedback?.rating &&
    autotriageFeedback_RatingFromJSON(autotriage?.feedback?.rating) ===
      AutotriageFeedback_Rating.BAD
  )
    return (
      <Text component="span" fz={12} style={{ fontStyle: "oblique 20deg" }}>
        We recorded that your team disagrees.
      </Text>
    );
  return (
    <Text fz={12} component="span">
      <Tooltip
        withinPortal
        position="bottom"
        label="Agreeing will automatically ignore the issue."
      >
        <UnstyledButton
          onClick={putAutotriageFeedback(AutotriageFeedback_Rating.GOOD)}
          style={{ fontSize: "inherit" }}
        >
          <Text fz={12} fw="bold" c="blue.6">
            Agree
          </Text>
        </UnstyledButton>
      </Tooltip>
      &nbsp;or&nbsp;
      <UnstyledButton
        onClick={putAutotriageFeedback(AutotriageFeedback_Rating.BAD)}
        style={{ fontSize: "inherit" }}
      >
        <Text fz={12} fw="bold" c="blue.6">
          Disagree
        </Text>
      </UnstyledButton>
    </Text>
  );
};

export const RuleGroupAutotriage = ({ autotriage }: Props) => {
  if (autotriage === null) return null;
  if (
    autotriage_VerdictFromJSON(autotriage.verdict) !==
    Autotriage_Verdict.FALSE_POSITIVE
  )
    return null;

  return (
    <Text
      c="dark.3"
      fz={12}
      ml={{ base: "sm", xl: 78 }} // distance from left edge to start of file path
      mr={{ base: "sm", xl: 0 }}
      mb={{ base: "xs", xl: 6 }}
      style={{ maxWidth: "100ch" }}
    >
      <FontAwesomeIcon icon={faRobot} fixedWidth />{" "}
      <Text fz={12} component="span" fw="bold">
        False positive?
      </Text>{" "}
      {autotriage.reason} <FeedbackButtons autotriage={autotriage} />
    </Text>
  );
};
